import { useCallback, useEffect, useRef, useState } from 'react';

import { AutoComplete } from 'primereact/autocomplete';
import { InputTextarea } from 'primereact/inputtextarea';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useLazyGetStorageLocationsQuery } from '@/app/services/locations';
import { useSearchAspirationSitesQuery } from '@/app/services/lookups';
import { addToast } from '@/app/slices/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DEBOUNCE_DELAY, SELECTION_LIMIT } from '@/constants';
import { formatEmbryoName } from '@/grid/value-formatters';
import { SortEnumType, StorageLocation } from '@/rtk-query.gql';
import { EmbryoDto, embryoServiceApi } from '@/services/embryo-service';

type LocationChangeDialogProps = {
  visible: boolean;
  onHide: () => void;
  selectedEmbryos: EmbryoDto[];
  onSuccess: (failedEmbryos?: EmbryoDto[]) => void;
};

export const LocationChangeDialog = ({
  visible,
  onHide,
  selectedEmbryos,
  onSuccess,
}: LocationChangeDialogProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [selectedSite, setSelectedSite] = useState<any | null>(null);
  const [selectedLocation, setSelectedLocation] =
    useState<StorageLocation | null>(null);
  const [locationInput, setLocationInput] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState<
    StorageLocation[]
  >([]);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  const { data: siteOptions = [] } = useSearchAspirationSitesQuery({
    where: {},
  });
  const [queryLocations] = useLazyGetStorageLocationsQuery();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const searchLocations = useCallback(
    (searchText: string, siteId?: string | null) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        const where: Record<string, any> = { stateCode: { eq: 0 } };
        if (searchText) {
          where.name = { contains: searchText };
        }
        if (siteId) {
          where.siteId = { eq: siteId };
        }
        const { data = [] } = await queryLocations({
          skip: 0,
          take: SELECTION_LIMIT,
          order: [{ name: SortEnumType.ASC }],
          where,
        });
        setLocationSuggestions(data);
      }, DEBOUNCE_DELAY);
    },
    [queryLocations],
  );

  useEffect(() => {
    if (!visible) {
      setSelectedSite(null);
      setSelectedLocation(null);
      setLocationInput('');
      setLocationSuggestions([]);
      setLocationError(null);
      setNotes('');
    }
  }, [visible]);

  const fromLocation =
    new Set(selectedEmbryos.map((e) => e.storageLocationName)).size > 1
      ? t('common.multiple')
      : (selectedEmbryos[0]?.storageLocationName ?? '');

  const fromSite =
    new Set(selectedEmbryos.map((e) => e.siteName)).size > 1
      ? t('common.multiple')
      : (selectedEmbryos[0]?.siteName ?? '');

  const getNewSite = (site: any) => site?.site?.id ?? site?.fertilizationSiteId;

  const validateSiteLocation = (
    site: any,
    location: StorageLocation | null,
  ) => {
    const newSiteId = getNewSite(site);
    if (site && location?.siteId && location.siteId !== newSiteId) {
      setLocationError(t('embryo.storageLocationDoesNotBelongToSite'));
    } else {
      setLocationError(null);
    }
  };

  const handleLocationSelect = (e: { value: StorageLocation }) => {
    const location = e.value;
    if (location.stateCode === 1) {
      setLocationError(t('embryo.storageLocationIsInactive'));
      setSelectedLocation(null);
      setLocationInput('');
      return;
    }
    setSelectedLocation(location);
    setLocationInput(location.name ?? '');
    setLocationError(null);

    if (location.siteId && siteOptions.length > 0) {
      const matchingSite = siteOptions.find(
        (s: any) => (s.site?.id ?? s.fertilizationSiteId) === location.siteId,
      );
      if (matchingSite) {
        setSelectedSite(matchingSite);
      }
    }
  };

  const handleSubmit = async () => {
    if (!selectedLocation) return;
    setIsSubmitting(true);
    try {
      const siteId = getNewSite(selectedSite);
      const editInputs = selectedEmbryos.map((embryo) => ({
        embryoId: embryo.id,
        storageLocationId: selectedLocation.id,
        siteId,
      }));
      const results = await embryoServiceApi.editEmbryos(editInputs as any);
      const failedEmbryos = selectedEmbryos.filter(
        (_, i) => !results[i]?.success,
      );
      const succeededEmbryos = selectedEmbryos.filter(
        (_, i) => results[i]?.success,
      );

      if (succeededEmbryos.length > 0 && notes.trim()) {
        await Promise.all(
          succeededEmbryos.map((embryo) =>
            embryoServiceApi.createAnnotation(embryo.id, notes.trim()),
          ),
        );
      }

      if (failedEmbryos.length === 0) {
        dispatch(
          addToast({
            severity: 'success',
            summary: t('common.success'),
            detail: t('embryo.embryoLocationsUpdatedSuccessfully'),
          }),
        );
        onSuccess();
        onHide();
        return;
      }

      const errorMessages = results
        .filter((r) => !r.success)
        .map((r) => r.message)
        .filter(Boolean);
      const uniqueErrors = [...new Set(errorMessages)];
      const toastSummary = uniqueErrors
        .map((msg) => {
          const count = errorMessages.filter((m) => m === msg).length;
          return count > 1 ? `${msg} (${count})` : msg;
        })
        .join('; ');

      if (succeededEmbryos.length === 0) {
        dispatch(
          addToast({
            severity: 'error',
            summary: t('common.error'),
            detail: toastSummary || t('embryo.failedToUpdateEmbryoLocations'),
          }),
        );
        return;
      }

      dispatch(
        addToast({
          severity: 'warn',
          summary: t('common.warning'),
          detail: `${succeededEmbryos.length} embryo(s) updated, ${failedEmbryos.length} failed. ${toastSummary}`,
        }),
      );
      onSuccess(failedEmbryos);
      onHide();
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail:
            error instanceof Error
              ? error.message
              : t('embryo.failedToUpdateEmbryoLocations'),
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={visible}
      onOpenChange={(o) => {
        if (!o) onHide();
      }}
    >
      <DialogContent
        className="md:max-w-[600px]"
        showCloseButton={!isSubmitting}
      >
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-3">
              <i className="pi pi-sort-alt text-xl" />
              <div className="flex flex-col">
                <span className="text-xl font-semibold">
                  {t('embryo.bulkLocationChange')}
                </span>
                <span className="text-sm font-normal text-gray-500">
                  {t('embryo.moveSelectedEmbryosToNewLocation')}
                </span>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-8 p-2">
          <div className="label-input-group">
            <label>
              {t('embryo.selectedEmbryosCount')} ({selectedEmbryos.length})
            </label>
            <div className="min-h-8 w-full rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {selectedEmbryos
                .map((e) => formatEmbryoName(e) || e.id)
                .join(', ')}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="min-w-0">
              <label>{t('embryo.fromLocation')}</label>
              <Input value={fromLocation} readOnly className="w-full" />
            </div>
            <div className="min-w-0">
              <label>{t('embryo.fromSite')}</label>
              <Input value={fromSite} readOnly className="w-full" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex min-w-0 flex-col gap-1">
              <label>{t('embryo.toLocation')}</label>
              <AutoComplete
                value={locationInput}
                suggestions={locationSuggestions}
                field="name"
                dropdown
                completeMethod={(e) =>
                  searchLocations(e.query, getNewSite(selectedSite))
                }
                onChange={(e) =>
                  setLocationInput(
                    typeof e.value === 'string'
                      ? e.value
                      : (e.value?.name ?? ''),
                  )
                }
                onSelect={(e) => handleLocationSelect({ value: e.value })}
                placeholder={t('embryo.selectLocation')}
                className="w-full"
              />
              {locationError && (
                <small className="p-error">{locationError}</small>
              )}
            </div>
            <div className="flex min-w-0 flex-col gap-1">
              <label>{t('embryo.toSite')}</label>
              <Select
                aria-label="Select"
                value={selectedSite}
                onChange={(newSite: any) => {
                  const site = newSite ?? null;
                  setSelectedSite(site);
                  validateSiteLocation(site, selectedLocation);
                }}
              >
                <SelectTrigger className="w-full min-w-0">
                  <SelectValue placeholder={t('site.selectSite')} />
                </SelectTrigger>
                <SelectContent>
                  {siteOptions.map((option: any, index: number) => (
                    <SelectItem key={index} value={option}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="label-input-group">
            <label>{t('embryo.notesOptional')}</label>
            <InputTextarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('embryo.addNotesAboutLocationChange')}
              rows={3}
              className="w-full"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onHide} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!selectedLocation || !selectedSite || !!locationError}
            onClick={handleSubmit}
          >
            {isSubmitting && (
              <i className="pi pi-spin pi-spinner mr-1.5 text-xs" />
            )}
            {t('embryo.updateLocation')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
